// assets/cart-items.js
(function () {
  if (customElements.get('cart-items')) {
    return;
  }

  const REGION_ATTRS = [
    'data-cart-drawer-items',
    'data-cart-drawer-count-text',
    'data-cart-drawer-total-label',
    'data-cart-drawer-goals-region',
    'data-cart-drawer-discounts-region',
    'data-cart-drawer-discount-count',
    'data-cart-page-items',
    'data-cart-page-count-text',
    'data-cart-page-goals-region',
    'data-cart-page-discounts-region',
    'data-cart-delivery-summary'
  ];

  // Cross-instance lock for the gift reconciler. On /cart both the drawer's
  // and the page's <cart-items> subscribe to cart:updated; the shared lock
  // ensures a threshold crossing fires exactly one /cart/add.js. Strategy is
  // drop-while-busy: cart:updated events arriving while a reconcile is in
  // flight are ignored; the next non-reconciler mutation re-publishes and
  // the reconciler re-evaluates.
  let reconciling = false;

  class CartItems extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.drawerOpenedUnsubscribe = null;
      this.addRequestUnsubscribe = null;
      this.cartUpdatedUnsubscribe = null;
      this.scope = null;
      this.handleClick = this.handleClick.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleScopeSubmit = this.handleScopeSubmit.bind(this);
      this.handleScopeClick = this.handleScopeClick.bind(this);
      this.handleLineMutate = this.handleLineMutate.bind(this);
      this.handleDrawerOpened = this.handleDrawerOpened.bind(this);
      this.handleAddRequest = this.handleAddRequest.bind(this);
      this.handleCartUpdated = this.handleCartUpdated.bind(this);
    }

    connectedCallback() {
      this.scope = this.closest('cart-drawer') || this.closest('[data-cart-page]') || null;
      this.addEventListener('click', this.handleClick);
      this.addEventListener('submit', this.handleSubmit);
      this.addEventListener('cart:line-mutate', this.handleLineMutate);
      if (this.scope) {
        this.scope.addEventListener('submit', this.handleScopeSubmit);
        this.scope.addEventListener('click', this.handleScopeClick);
      }
      if (window.themePubsub && typeof window.themePubsub.subscribe === 'function') {
        this.drawerOpenedUnsubscribe = window.themePubsub.subscribe(
          'cart:drawer:opened',
          this.handleDrawerOpened
        );
        this.addRequestUnsubscribe = window.themePubsub.subscribe(
          'cart:add-request',
          this.handleAddRequest
        );
        this.cartUpdatedUnsubscribe = window.themePubsub.subscribe(
          'cart:updated',
          this.handleCartUpdated
        );
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('submit', this.handleSubmit);
      this.removeEventListener('cart:line-mutate', this.handleLineMutate);
      if (this.scope) {
        this.scope.removeEventListener('submit', this.handleScopeSubmit);
        this.scope.removeEventListener('click', this.handleScopeClick);
        this.scope = null;
      }
      if (this.drawerOpenedUnsubscribe) {
        this.drawerOpenedUnsubscribe();
        this.drawerOpenedUnsubscribe = null;
      }
      if (this.addRequestUnsubscribe) {
        this.addRequestUnsubscribe();
        this.addRequestUnsubscribe = null;
      }
      if (this.cartUpdatedUnsubscribe) {
        this.cartUpdatedUnsubscribe();
        this.cartUpdatedUnsubscribe = null;
      }
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
    }

    handleClick(event) {
      const target = event.target.closest('[data-cart-update]');
      if (!target || !this.contains(target)) {
        return;
      }
      event.preventDefault();
      if (target.disabled) {
        return;
      }
      const key = target.dataset.cartKey;
      const quantity = parseInt(target.dataset.cartQty, 10);
      if (!key || Number.isNaN(quantity)) {
        return;
      }
      const max = parseInt(target.dataset.cartMax || '', 10);
      if (!Number.isNaN(max) && max > 0 && quantity > max) {
        target.disabled = true;
        return;
      }
      this.changeItem(key, quantity);
    }

    handleSubmit(event) {
      const form = event.target;
      // Let the checkout form post to Shopify normally; only intercept the
      // line-item/quantity form so it doesn't trigger a full-page reload.
      if (
        form &&
        typeof form.matches === 'function' &&
        form.matches('.cart-page__checkout-form, .cart-drawer__checkout-form')
      ) {
        return;
      }
      if (event.submitter && event.submitter.name === 'checkout') {
        return;
      }
      event.preventDefault();
    }

    handleScopeSubmit(event) {
      const form = event.target;
      if (!form || typeof form.matches !== 'function') {
        return;
      }
      if (!form.matches('[data-cart-discount-form]')) {
        return;
      }
      if (this.scope && !this.scope.contains(form)) {
        return;
      }
      event.preventDefault();
      this.applyDiscount(form);
    }

    handleScopeClick(event) {
      const button = event.target.closest('[data-cart-discount-clear]');
      if (!button || (this.scope && !this.scope.contains(button))) {
        return;
      }
      event.preventDefault();
      this.clearDiscount(button);
    }

    clearDiscount(button) {
      const sectionId = this.getSectionId();
      if (!sectionId) {
        return;
      }
      const removeCode = ((button && button.dataset.discountCode) || '').trim();
      if (!removeCode) {
        return;
      }
      const chip = button ? button.closest('.cart-drawer__applied-discount, .cart-page__applied-discount') : null;
      if (chip) {
        chip.classList.add('is-pending');
      }
      if (button) {
        button.disabled = true;
      }

      const peer = this.getPeer();
      this.fetchCart()
        .then((beforeCart) => {
          const survivors = this.getAppliedCodes(beforeCart).filter(
            (code) => code.toLowerCase() !== removeCode.toLowerCase()
          );
          return this.postDiscount(survivors.join(','), this.buildSectionsList(sectionId, peer));
        })
        .then((data) => {
          this.applySectionsResponse(data, sectionId, peer);
          if (data && typeof data.item_count === 'number') {
            this.syncBadges(data.item_count);
          }
          this.notify(this.dataset.textDiscountRemoved, 'success');
          if (window.themePubsub && typeof window.themePubsub.publish === 'function') {
            window.themePubsub.publish('cart:updated', { cart: data });
          }
        })
        .catch(() => {
          if (chip) {
            chip.classList.remove('is-pending');
          }
          if (button) {
            button.disabled = false;
          }
          this.resyncFromServer(sectionId);
          this.notify(this.dataset.textDiscountError, 'error');
        });
    }

    getAppliedCodes(cart) {
      const apps = (cart && cart.cart_level_discount_applications) || [];
      return apps
        .filter((app) => app && app.type === 'discount_code')
        .map((app) => String(app.title || ''))
        .filter(Boolean);
    }

    postDiscount(value, sections) {
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          discount: value,
          sections: sections,
          sections_url: window.location.pathname
        })
      }).then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error((data && (data.description || data.message)) || 'cart_error');
        }
        return data;
      });
    }

    handleLineMutate(event) {
      if (!event || !event.detail) {
        return;
      }
      const key = event.detail.key;
      const quantity = event.detail.quantity;
      if (!key || typeof quantity !== 'number' || Number.isNaN(quantity)) {
        return;
      }
      this.changeItem(key, quantity);
    }

    handleDrawerOpened() {
    }

    handleCartUpdated(payload) {
      if (reconciling) {
        return;
      }
      if (!payload || !payload.cart) {
        return;
      }
      this.reconcileGift(payload.cart);
    }

    reconcileGift(cart) {
      const root = this.scope || document;
      const node = root.querySelector('[data-cart-goals][data-cart-rewards-gift-enabled="true"]');
      if (!node) {
        return;
      }
      const threshold = parseInt(node.dataset.cartRewardsGiftThresholdCents, 10);
      const variantId = parseInt(node.dataset.cartRewardsGiftVariantId, 10);
      const available = node.dataset.cartRewardsGiftAvailable === 'true';
      if (!threshold || !variantId) {
        return;
      }

      const subtotal = cart && typeof cart.total_price === 'number' ? cart.total_price : 0;
      const items = (cart && cart.items) || [];
      const giftLine = items.find(function (item) {
        return item && item.variant_id === variantId;
      });
      const qualifies = subtotal >= threshold;
      const giftPresent = !!giftLine;

      if (qualifies && !giftPresent && available) {
        this.addGiftLine(variantId);
      } else if (!qualifies && giftPresent) {
        this.removeGiftLine(giftLine.key);
      }
    }

    addGiftLine(variantId) {
      const sectionId = this.getSectionId();
      if (!sectionId) {
        return;
      }
      const peer = this.getPeer();
      reconciling = true;
      const formData = new FormData();
      formData.set('id', String(variantId));
      formData.set('quantity', '1');
      formData.set('sections', (this.buildSectionsList(sectionId, peer) || [sectionId]).join(','));
      formData.set('sections_url', window.location.pathname);
      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: formData
      })
        .then(async (response) => {
          const data = await response.json().catch(() => ({}));
          if (!response.ok) {
            return null;
          }
          return data;
        })
        .then((data) => {
          this.applyReconcilerResponse(data, sectionId, peer);
        })
        .catch(() => {})
        .finally(() => {
          reconciling = false;
        });
    }

    removeGiftLine(key) {
      const sectionId = this.getSectionId();
      if (!sectionId || !key) {
        return;
      }
      const peer = this.getPeer();
      reconciling = true;
      fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          id: key,
          quantity: 0,
          sections: this.buildSectionsList(sectionId, peer),
          sections_url: window.location.pathname
        })
      })
        .then(async (response) => {
          const data = await response.json().catch(() => ({}));
          if (!response.ok) {
            return null;
          }
          return data;
        })
        .then((data) => {
          this.applyReconcilerResponse(data, sectionId, peer);
        })
        .catch(() => {})
        .finally(() => {
          reconciling = false;
        });
    }

    applyReconcilerResponse(data, sectionId, peer) {
      if (!data) {
        return;
      }
      this.applySectionsResponse(data, sectionId, peer);
      if (typeof data.item_count === 'number') {
        this.syncBadges(data.item_count);
      }
      if (window.themePubsub && typeof window.themePubsub.publish === 'function') {
        window.themePubsub.publish('cart:updated', { cart: data });
      }
    }

    handleAddRequest(payload) {
      if (!payload || !(payload.formData instanceof FormData)) {
        return;
      }
      const sectionId = this.getSectionId();
      const peer = this.getPeer();
      const formData = payload.formData;
      if (sectionId) {
        const sections = this.buildSectionsList(sectionId, peer) || [sectionId];
        formData.set('sections', sections.join(','));
        formData.set('sections_url', window.location.pathname);
      }
      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: formData
      })
        .then(async (response) => {
          const data = await response.json().catch(() => ({}));
          if (!response.ok) {
            const message = data && (data.description || data.message);
            const error = new Error(message || 'cart_error');
            error.isCartServerError = true;
            error.serverMessage = message || '';
            throw error;
          }
          return data;
        })
        .then((data) => {
          if (data && data.sections && sectionId) {
            this.applySectionsResponse(data, sectionId, peer);
            this.syncBadgesFromRegion();
          }
          if (!window.themePubsub || typeof window.themePubsub.publish !== 'function') {
            return;
          }
          window.themePubsub.publish('cart:drawer:open-request', {});
          // add.js answers with the added line only; subscribers such as the
          // gift reconciler need the full cart (items, total_price).
          return fetch('/cart.js', {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' }
          })
            .then((response) => (response.ok ? response.json() : null))
            .catch(() => null)
            .then((cart) => {
              const isFullCart = cart && Array.isArray(cart.items) && typeof cart.total_price === 'number';
              window.themePubsub.publish('cart:updated', { cart: isFullCart ? cart : data });
            });
        })
        .catch((error) => {
          if (error && error.name === 'AbortError') {
            return;
          }
          const fallback = this.dataset.textErrorGeneric || "Couldn't update cart. Check your connection.";
          const message = error && error.isCartServerError && error.serverMessage
            ? error.serverMessage
            : fallback;
          if (window.themePubsub && typeof window.themePubsub.publish === 'function') {
            window.themePubsub.publish('cart:notification', { message: message, status: 'error' });
          }
        })
        .finally(() => {
          if (typeof payload.onSettled === 'function') {
            payload.onSettled();
          }
        });
    }

    getSectionId() {
      if (this.dataset.sectionId) {
        return this.dataset.sectionId;
      }
      const drawer = this.closest('cart-drawer');
      if (drawer) {
        const candidate = drawer.querySelector('[data-section-id]');
        if (candidate && candidate.dataset.sectionId) {
          return candidate.dataset.sectionId;
        }
      }
      return null;
    }

    // On /cart both surfaces (drawer + page) are rendered in the same document.
    // We refresh both in a single SRAPI call so a mutation from one surface
    // doesn't leave the other stale.
    getPeer() {
      const all = document.querySelectorAll('cart-items');
      for (let i = 0; i < all.length; i++) {
        const other = all[i];
        if (other === this) continue;
        const scope =
          other.scope ||
          other.closest('cart-drawer') ||
          other.closest('[data-cart-page]') ||
          null;
        const sectionId = other.dataset && other.dataset.sectionId;
        if (sectionId && scope && scope !== this.scope) {
          return { instance: other, scope: scope, sectionId: sectionId };
        }
      }
      return null;
    }

    buildSectionsList(sectionId, peer) {
      if (!sectionId) return null;
      return peer ? [sectionId, peer.sectionId] : [sectionId];
    }

    applySectionsResponse(data, sectionId, peer) {
      if (!data || !data.sections) return;
      if (sectionId && data.sections[sectionId]) {
        this.replaceRegions(data.sections[sectionId]);
      }
      if (peer && data.sections[peer.sectionId]) {
        this.replaceRegions(data.sections[peer.sectionId], peer.scope);
      }
    }

    changeItem(key, quantity) {
      const sectionId = this.getSectionId();
      if (!sectionId) {
        return;
      }

      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();
      const signal = this.abortController.signal;

      const peer = this.getPeer();
      const body = {
        id: key,
        quantity: quantity,
        sections: this.buildSectionsList(sectionId, peer),
        sections_url: window.location.pathname
      };

      fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: JSON.stringify(body),
        signal: signal
      })
        .then(async (response) => {
          const data = await response.json().catch(() => ({}));
          if (!response.ok) {
            const message = data && (data.description || data.message);
            const error = new Error(message || 'cart_error');
            error.isCartServerError = true;
            error.serverMessage = message || '';
            throw error;
          }
          return data;
        })
        .then((data) => {
          if (!data) {
            return;
          }
          // Publish settled BEFORE replaceRegions so quantity-input can flush
          // any buffered desired-qty (rapid clicks) while its DOM is still alive.
          if (window.themePubsub && typeof window.themePubsub.publish === 'function') {
            window.themePubsub.publish('cart:line-mutate-settled', { key: key });
          }
          this.applySectionsResponse(data, sectionId, peer);
          if (typeof data.item_count === 'number') {
            this.syncBadges(data.item_count);
          }
          if (window.themePubsub && typeof window.themePubsub.publish === 'function') {
            window.themePubsub.publish('cart:updated', { cart: data });
          }
        })
        .catch((error) => {
          if (error && error.name === 'AbortError') {
            return;
          }
          if (window.themePubsub && typeof window.themePubsub.publish === 'function') {
            window.themePubsub.publish('cart:line-mutate-settled', { key: key });
          }
          const fallback = this.dataset.textErrorGeneric || "Couldn't update cart. Check your connection.";
          const message = error && error.isCartServerError && error.serverMessage
            ? error.serverMessage
            : fallback;
          if (window.themePubsub && typeof window.themePubsub.publish === 'function') {
            window.themePubsub.publish('cart:notification', { message: message, status: 'error' });
          }
          this.resyncFromServer(sectionId);
        });
    }

    applyDiscount(form) {
      const sectionId = this.getSectionId();
      if (!sectionId) {
        return;
      }
      const input = form.querySelector('[data-cart-discount-input]');
      const code = (input && typeof input.value === 'string' ? input.value : '').trim();

      if (!code) {
        this.notify(this.dataset.textDiscountEmpty, 'error');
        return;
      }

      const submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn) {
        submitBtn.disabled = true;
      }

      const peer = this.getPeer();
      this.fetchCart()
        .then((beforeCart) => {
          const existing = this.getAppliedCodes(beforeCart);
          const lc = code.toLowerCase();
          const alreadyApplied = existing.some((c) => c.toLowerCase() === lc);
          const merged = alreadyApplied ? existing : existing.concat([code]);
          return this.postDiscount(merged.join(','), this.buildSectionsList(sectionId, peer)).then((cart) => ({
            beforeCodes: existing,
            cart: cart
          }));
        })
        .then((result) => {
          const cart = result.cart || {};
          const after = this.getAppliedCodes(cart).map((c) => c.toLowerCase());
          const lc = code.toLowerCase();
          const newAccepted = after.indexOf(lc) !== -1;
          const droppedExisting = result.beforeCodes.filter(
            (c) => after.indexOf(c.toLowerCase()) === -1
          );

          this.commitDiscountResult(cart, sectionId, peer, input, submitBtn);

          let message;
          let status;
          if (newAccepted && droppedExisting.length === 0) {
            message = this.dataset.textDiscountSuccess;
            status = 'success';
          } else if (
            (newAccepted && droppedExisting.length > 0) ||
            (!newAccepted && result.beforeCodes.length > 0)
          ) {
            message = this.dataset.textDiscountCombineError;
            status = 'error';
          } else {
            message = this.dataset.textDiscountError;
            status = 'error';
          }
          this.notify(message, status);
        })
        .catch(() => {
          if (submitBtn) {
            submitBtn.disabled = false;
          }
          this.resyncFromServer(sectionId);
          this.notify(this.dataset.textDiscountError, 'error');
        });
    }

    commitDiscountResult(cart, sectionId, peer, input, submitBtn) {
      this.applySectionsResponse(cart, sectionId, peer);
      if (cart && typeof cart.item_count === 'number') {
        this.syncBadges(cart.item_count);
      }
      if (input) {
        input.value = '';
      }
      if (submitBtn) {
        submitBtn.disabled = false;
      }
      if (window.themePubsub && typeof window.themePubsub.publish === 'function') {
        window.themePubsub.publish('cart:updated', { cart: cart });
      }
    }

    fetchCart() {
      return fetch('/cart.js', {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      }).then(function (response) {
        return response.json();
      });
    }

    hasDiscounts(cart) {
      if (!cart) {
        return false;
      }
      if ((cart.total_discount || 0) > 0) {
        return true;
      }
      if ((cart.cart_level_discount_applications || []).length > 0) {
        return true;
      }
      return (cart.items || []).some(function (item) {
        return (item.total_discount || 0) > 0 || ((item.discounts || []).length > 0);
      });
    }

    getDiscountTexts(cart) {
      const texts = [];
      (cart.cart_level_discount_applications || []).forEach(function (discount) {
        if (discount.title) {
          texts.push(String(discount.title).toLowerCase());
        }
        if (discount.code) {
          texts.push(String(discount.code).toLowerCase());
        }
      });
      (cart.items || []).forEach(function (item) {
        (item.discounts || []).forEach(function (discount) {
          if (discount.title) {
            texts.push(String(discount.title).toLowerCase());
          }
          if (discount.code) {
            texts.push(String(discount.code).toLowerCase());
          }
        });
      });
      return texts;
    }

    discountSignature(cart) {
      return JSON.stringify({
        total_discount: cart.total_discount || 0,
        cart_level_discount_applications: (cart.cart_level_discount_applications || []).map(function (discount) {
          return {
            title: discount.title || '',
            code: discount.code || '',
            amount: discount.total_allocated_amount || 0
          };
        }),
        item_discounts: (cart.items || []).map(function (item) {
          return {
            key: item.key,
            total_discount: item.total_discount || 0,
            discounts: (item.discounts || []).map(function (discount) {
              return { title: discount.title || '', code: discount.code || '' };
            })
          };
        })
      });
    }

    notify(message, status) {
      if (!message) {
        return;
      }
      if (window.themePubsub && typeof window.themePubsub.publish === 'function') {
        window.themePubsub.publish('cart:notification', { message: message, status: status || 'success' });
      }
    }

    resyncFromServer(sectionId) {
      const resolvedSectionId = sectionId || this.getSectionId();
      if (!resolvedSectionId) {
        return;
      }
      const peer = this.getPeer();
      const sections = this.buildSectionsList(resolvedSectionId, peer) || [resolvedSectionId];
      const url = '/cart.js?sections=' + sections.map(encodeURIComponent).join(',');
      fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      })
        .then((response) => response.json())
        .then((data) => {
          if (!data) {
            return;
          }
          this.applySectionsResponse(data, resolvedSectionId, peer);
          if (typeof data.item_count === 'number') {
            this.syncBadges(data.item_count);
          }
        })
        .catch(() => {});
    }

    syncBadges(itemCount) {
      const badges = document.querySelectorAll('[data-cart-count]');
      badges.forEach((badge) => {
        badge.textContent = String(itemCount);
        badge.hidden = itemCount === 0;
      });
    }

    syncBadgesFromRegion() {
      const scope = this.scope || this.closest('cart-drawer') || this.closest('[data-cart-page]') || document;
      const numberEl = scope.querySelector('[data-cart-drawer-count-text] .cart-drawer__count-number');
      if (!numberEl) {
        return;
      }
      const parsed = parseInt((numberEl.textContent || '').replace(/\D+/g, ''), 10);
      if (Number.isNaN(parsed)) {
        return;
      }
      this.syncBadges(parsed);
    }

    replaceRegions(html, scopeOverride) {
      const template = document.createElement('template');
      template.innerHTML = html;
      const fresh = template.content;

      const scope =
        scopeOverride ||
        this.scope ||
        this.closest('cart-drawer') ||
        this.closest('[data-cart-page]') ||
        document;

      // Items wrapper is the scrollable region on both surfaces; capture its
      // scrollTop before replacement so we can restore it on the fresh node.
      const itemsSelector = '[data-cart-drawer-items], [data-cart-page-items]';
      const liveItems = scope.querySelector(itemsSelector);
      const scrollTop = liveItems ? liveItems.scrollTop : 0;

      REGION_ATTRS.forEach((attr) => {
        const selector = '[' + attr + ']';
        const liveTargets = scope.querySelectorAll(selector);
        const freshTargets = fresh.querySelectorAll(selector);
        liveTargets.forEach((liveEl, index) => {
          const freshEl = freshTargets[index];
          if (!freshEl) {
            return;
          }
          liveEl.replaceWith(freshEl.cloneNode(true));
        });
      });

      if (scrollTop > 0) {
        const newItems = scope.querySelector(itemsSelector);
        if (newItems) {
          newItems.scrollTop = scrollTop;
        }
      }

      // Swapped-in delivery summaries arrive with their no-JS text; fill in the dates.
      if (window.themeDeliveryEstimate) {
        window.themeDeliveryEstimate.refresh(scope);
      }
    }
  }

  customElements.define('cart-items', CartItems);
})();
